import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API } from '../App';
import { Loader2, DollarSign, Landmark, Briefcase, PiggyBank, TrendingUp, Shield, AlertTriangle, ArrowRight } from 'lucide-react';
import { useYear } from '../contexts/YearContext';
import YearSelector from './YearSelector';
import { useAccountBalances } from '../hooks/useAccountBalances';
import { HelpSection, HelpP, HelpList, HelpRule, HelpCallout, HelpHeading } from './HelpSection';
import ExportButton from './ExportButton';
import MoneyFlowExplainer from './MoneyFlowExplainer';

function AccountBalances() {
  const { selectedYear } = useYear();
  const { data: balances, loading: balancesLoading } = useAccountBalances({ year: selectedYear });
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  // Per-account Expected-vs-Actual deltas, loaded from the same canonical engine
  // that Dashboard / Account Health use. Comparison wording only appears when
  // the user has actually entered Actual Balances in Settings.
  const [comparison, setComparison] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setPlanLoading(true);
        setComparisonLoading(true);
        const [planRes, compRes] = await Promise.all([
          axios.get(`${API}/financial-plan`),
          axios.get(`${API}/settings/balance-comparison?year=${selectedYear}`).catch(() => null),
        ]);
        if (cancelled) return;
        setPlan(planRes.data);
        // API shape: { comparison: {personal, business, taxes, savings, custom_allocations}, warnings, last_updated }
        setComparison(compRes?.data?.comparison || null);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('Error loading plan:', err);
        }
      } finally {
        if (!cancelled) {
          setPlanLoading(false);
          setComparisonLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedYear]);

  const loading = balancesLoading || planLoading || comparisonLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="balances-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const totalBalance = balances
    ? balances.operations + balances.taxes + balances.business_growth + (balances.savings || 0) + (balances.buffer || 0) +
      (balances.custom_allocations ? Object.values(balances.custom_allocations).reduce((sum, val) => sum + val, 0) : 0)
    : 0;

  // Only show Actual-vs-Expected comparison chips when the user has actually
  // entered Actual Balances in Settings → Balances. Otherwise the zero-valued
  // actuals would produce a misleading "$X below expected" on every card.
  const hasActuals = !!comparison && (
    (comparison.personal?.actual || 0) !== 0 ||
    (comparison.business?.actual || 0) !== 0 ||
    (comparison.taxes?.actual    || 0) !== 0 ||
    (comparison.savings?.actual  || 0) !== 0 ||
    (comparison.buffer?.actual   || 0) !== 0 ||
    Object.values(comparison.custom_allocations || {}).some((c) => (c?.actual || 0) !== 0)
  );
  const compFor = (key) => hasActuals ? comparison?.[key] : null;
  const compForCustom = (name) => hasActuals ? comparison?.custom_allocations?.[name] : null;

  return (
    <div className="space-y-6" data-testid="account-balances">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Account Balances</h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              <strong>Expected Current Balance</strong> for each account — what the system math says should be there (<span className="text-gray-500 dark:text-gray-400">allocated − tagged expenses</span>).
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Bank reality lives in <Link to="/settings?tab=balances" className="underline decoration-dotted underline-offset-2 hover:text-gray-700 dark:hover:text-gray-200" data-testid="accounts-goto-actuals">Settings → Balances</Link> as <em>Actual Balances</em>. Any gap between the two is flagged below.
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <YearSelector />
            <ExportButton
              endpoint="/exports/balances.csv"
              params={{ year: selectedYear }}
              filename={`papertrail-balances-${selectedYear}.csv`}
              label="Export CSV"
              testId="export-balances-csv-btn"
            />
          </div>
        </div>
      </div>

      {/* Total Balance */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg shadow-lg p-8">
        <div className="flex items-center space-x-3 mb-2">
          <DollarSign className="w-8 h-8" />
          <h2 className="text-xl font-semibold">Total Expected Current Balance</h2>
        </div>
        <p className="text-5xl font-bold" data-testid="total-balance">
          ${totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
        <p className="mt-3 text-sm text-blue-100/90">
          Sum of <strong>Expected Current Balances</strong> (allocated − tagged expenses) across Personal, Business, Taxes, Savings, Buffer
          {balances?.custom_allocations && Object.keys(balances.custom_allocations).length > 0 ? ', and your custom accounts' : ''}. This is a system number, not the total in your bank.
        </p>
      </div>

      {/* Individual Accounts */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6">
        {/* Personal Account */}
        <AccountCard
          title="Personal Account"
          description={`Living expenses & bills (${plan?.allocation?.personal || 55}%)`}
          balance={balances?.operations || 0}
          comparison={compFor('personal')}
          icon={DollarSign}
          color="green"
          testId="account-personal"
        />

        {/* Business Account */}
        <AccountCard
          title="Business Account"
          description={`Marketing, leads & business expenses (${plan?.allocation?.business || 20}%)`}
          balance={balances?.business_growth || 0}
          comparison={compFor('business')}
          icon={TrendingUp}
          color="blue"
          testId="account-business"
        />

        {/* Taxes */}
        <AccountCard
          title="Taxes Account"
          description={`Tax reserve (${plan?.allocation?.taxes || 20}%) — allocated − tax payments`}
          balance={balances?.taxes || 0}
          comparison={compFor('taxes')}
          icon={Briefcase}
          color="red"
          testId="account-taxes"
        />

        {/* Savings Account */}
        <AccountCard
          title="Savings Account"
          description={`Emergency fund & future goals (${plan?.allocation?.savings || 5}%)`}
          balance={balances?.savings || 0}
          comparison={compFor('savings')}
          icon={Shield}
          color="yellow"
          testId="account-savings"
        />

        {/* Buffer Account — first-class bucket since Feb 2026. Holds
            unallocated capital retained outside the core plan buckets. */}
        <AccountCard
          title="Buffer Account"
          description="Unallocated capital retained outside plan buckets"
          balance={balances?.buffer || 0}
          comparison={compFor('buffer')}
          icon={Landmark}
          color="cyan"
          testId="account-buffer"
        />
        
        {/* Custom Allocations */}
        {balances?.custom_allocations && Object.keys(balances.custom_allocations).length > 0 && 
          Object.entries(balances.custom_allocations).map(([name, balance]) => (
            <AccountCard
              key={name}
              title={name}
              description="Custom allocation account"
              balance={balance || 0}
              comparison={compForCustom(name)}
              icon={PiggyBank}
              color="purple"
              testId={`account-custom-${name}`}
            />
          ))
        }
      </div>

      {/* Account System Guide */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">How Your System Works</h2>
        <div className="space-y-4">
          <GuideItem
            number="1"
            title="Income Hub"
            description="All income flows into your Business Account first."
          />
          <GuideItem
            number="2"
            title="Automatic Allocation"
            description={`Your income is split based on your selected percentages: ${plan?.allocation?.personal || 55}% Personal, ${plan?.allocation?.business || 20}% Business, ${plan?.allocation?.taxes || 20}% Taxes, and ${plan?.allocation?.savings || 5}% Savings.`}
          />
          <GuideItem
            number="3"
            title="Personal Account"
            description="Covers your living expenses and take-home pay."
          />
          <GuideItem
            number="4"
            title="Business + Taxes + Savings"
            description="Funds operations, protects you from taxes, and builds long-term wealth."
          />
        </div>
      </div>

      {/* Money flow explainer — framing for the Allocation Breakdown that follows */}
      <MoneyFlowExplainer testId="money-flow-accounts" compact />

      {/* Allocation Breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Current Allocation Breakdown</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          How your <strong>Expected Current Balances</strong> stack up against each other right now
          (<span className="text-gray-500 dark:text-gray-400">not your plan percentages, and not your bank actuals</span>).
        </p>
        <NegativeBalanceNotice balances={balances} />
        <div className="space-y-3">
          <AllocationBar
            label="Personal Account"
            amount={balances?.operations || 0}
            percentage={totalBalance > 0 ? ((balances?.operations || 0) / totalBalance) * 100 : 0}
            color="green"
          />
          <AllocationBar
            label="Business Account"
            amount={balances?.business_growth || 0}
            percentage={totalBalance > 0 ? ((balances?.business_growth || 0) / totalBalance) * 100 : 0}
            color="blue"
          />
          <AllocationBar
            label="Taxes Account"
            amount={balances?.taxes || 0}
            percentage={totalBalance > 0 ? ((balances?.taxes || 0) / totalBalance) * 100 : 0}
            color="red"
          />
          <AllocationBar
            label="Savings Account"
            amount={balances?.savings || 0}
            percentage={totalBalance > 0 ? ((balances?.savings || 0) / totalBalance) * 100 : 0}
            color="yellow"
          />
          <AllocationBar
            label="Buffer Account"
            amount={balances?.buffer || 0}
            percentage={totalBalance > 0 ? ((balances?.buffer || 0) / totalBalance) * 100 : 0}
            color="cyan"
          />
        </div>
      </div>

      {/* How this works */}
      <HelpSection title="💰 How This Page Works" testId="help-accounts">
        <HelpP>
          Every number on this page is an <strong>Expected Current Balance</strong> — what the system math says
          should be in each account based on your allocations and the expenses you&apos;ve tagged.
        </HelpP>
        <HelpRule />
        <HelpHeading>🧠 The formula:</HelpHeading>
        <HelpCallout tone="blue">
          Money allocated into an account<br />
          − Money spent from that account<br />
          = <strong>Expected Current Balance</strong>
        </HelpCallout>
        <HelpRule />
        <HelpHeading>🏦 Expected vs Actual:</HelpHeading>
        <HelpList
          items={[
            <><strong>Expected Current Balance</strong> (this page) → system math, always derived</>,
            <><strong>Actual Balance</strong> (Settings → Balances) → what your bank actually shows, entered by you</>,
            <>If you&apos;ve entered Actuals, each card shows a small <em>&quot;Actual $X above / below expected&quot;</em> chip so the gap is obvious</>,
          ]}
        />
        <HelpRule />
        <HelpHeading>📊 What each account means:</HelpHeading>
        <HelpList
          items={[
            <><strong>Personal</strong> → Living expenses and take-home</>,
            <><strong>Business</strong> → Operating costs and growth</>,
            <><strong>Taxes</strong> → Reserved for taxes</>,
            <><strong>Savings</strong> → Emergency fund and long-term wealth</>,
          ]}
        />
        <HelpRule />
        <HelpHeading>⚠️ If an account is negative:</HelpHeading>
        <HelpP>It means more has been spent (tagged) from it than was allocated. The negative number is shown honestly; nothing is hidden.</HelpP>
        <HelpP muted>Use Rebalance on the Dashboard to bring the system back into alignment.</HelpP>
        <HelpRule />
        <HelpHeading>📉 Why percentages may look off:</HelpHeading>
        <HelpP>The Current Allocation Breakdown bars reflect <strong>Expected Current Balances</strong>, not your plan. If one account is negative, percentages can look distorted.</HelpP>
        <HelpRule />
        <HelpCallout tone="emerald">
          🎯 <strong>Simple way to think about it:</strong>
          <br />This page = where your money <em>should</em> be (system math)
          <br />Settings → Balances = where your money <em>actually</em> is (bank reality)
          <br />Rebalancing = how you close the gap between the two
        </HelpCallout>
      </HelpSection>
    </div>
  );
}

function NegativeBalanceNotice({ balances }) {
  if (!balances) return null;

  const businessNegative = (balances.business_growth ?? 0) < 0;
  const anyNegative =
    businessNegative ||
    (balances.operations ?? 0) < 0 ||
    (balances.taxes ?? 0) < 0 ||
    (balances.buffer ?? 0) < 0 ||
    Object.values(balances.custom_allocations || {}).some((v) => v < 0);

  if (!anyNegative) return null;

  const title = businessNegative
    ? 'Business account is negative'
    : 'One or more accounts are negative';
  const detail = businessNegative
    ? 'Allocation percentages may look distorted. Bring this account back to zero or rebalance to restore accuracy.'
    : 'Percentages may not reflect your true allocation. Rebalance or adjust balances to restore accuracy.';

  return (
    <div
      role="status"
      data-testid="negative-balance-notice"
      className="mb-4 flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3"
    >
      <AlertTriangle
        className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-300"
        aria-hidden="true"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">{title}</p>
        <p className="text-xs text-amber-700 dark:text-amber-300/90 mt-0.5">{detail}</p>
      </div>
      <Link
        to="/"
        data-testid="negative-balance-rebalance-btn"
        className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-500/20 hover:bg-amber-500/30 text-amber-800 dark:text-amber-100 border border-amber-500/40 transition-colors"
      >
        Rebalance Now
        <ArrowRight className="w-3 h-3" aria-hidden="true" />
      </Link>
    </div>
  );
}

function AccountCard({ title, description, balance, comparison, icon: Icon, color, testId }) {
  const colorClasses = {
    purple: {
      light: 'bg-purple-600',
      dark: 'dark:bg-gradient-to-r dark:from-purple-500 dark:to-purple-700'
    },
    green: {
      light: 'bg-green-600',
      dark: 'dark:bg-gradient-to-r dark:from-green-500 dark:to-green-700'
    },
    red: {
      light: 'bg-red-600',
      dark: 'dark:bg-gradient-to-r dark:from-red-500 dark:to-red-700'
    },
    blue: {
      light: 'bg-blue-600',
      dark: 'dark:bg-gradient-to-r dark:from-blue-500 dark:to-blue-700'
    },
    cyan: {
      light: 'bg-cyan-600',
      dark: 'dark:bg-gradient-to-r dark:from-cyan-500 dark:to-cyan-700'
    },
    yellow: {
      light: 'bg-yellow-600',
      dark: 'dark:bg-gradient-to-r dark:from-yellow-500 dark:to-yellow-700'
    }
  };

  // Raw signed value — negative balances are shown honestly as e.g. "-$450.00"
  // so the math can't be misread as "$450 in the account".
  const isNegative = balance < 0;
  const formatted = `${isNegative ? '-' : ''}$${Math.abs(balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Comparison chip is only rendered when the user has Actual Balances on file
  // AND the delta is material (>= $1). This keeps the page clean for users who
  // haven't set up actuals yet.
  const diff = comparison ? Number(comparison.difference || 0) : 0;
  const actual = comparison ? Number(comparison.actual || 0) : 0;
  const MATERIAL = 1;
  let deltaChip = null;
  if (comparison && Math.abs(diff) >= MATERIAL) {
    const above = diff > 0;
    const abs = Math.abs(diff).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    deltaChip = (
      <span
        data-testid={`${testId}-vs-actual`}
        data-delta-direction={above ? 'above' : 'below'}
        title={`Actual: $${actual.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full border ${
          above
            ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
            : 'text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30'
        }`}
      >
        Actual ${abs} {above ? 'above' : 'below'} expected
      </span>
    );
  } else if (comparison && Math.abs(diff) < MATERIAL) {
    deltaChip = (
      <span
        data-testid={`${testId}-vs-actual`}
        data-delta-direction="aligned"
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full border text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/30"
      >
        Actual matches expected
      </span>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden" data-testid={testId}>
      <div className={`${colorClasses[color]?.light} ${colorClasses[color]?.dark} text-white p-4`}>
        <div className="flex items-center space-x-2 mb-2">
          <Icon className="w-6 h-6" />
          <h3 className="text-lg font-bold">{title}</h3>
        </div>
        <p className="text-sm font-medium">{description}</p>
      </div>
      <div className="p-6">
        <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400 mb-1">
          Expected Current Balance
        </p>
        <p
          className={`text-4xl font-bold ${isNegative ? 'text-rose-600 dark:text-rose-400' : 'text-gray-900 dark:text-white'}`}
          data-testid={`${testId}-expected`}
        >
          {formatted}
        </p>
        {deltaChip && <div className="mt-3">{deltaChip}</div>}
      </div>
    </div>
  );
}

function GuideItem({ number, title, description }) {
  return (
    <div className="flex space-x-4">
      <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
        {number}
      </div>
      <div>
        <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 dark:text-gray-300">{description}</p>
      </div>
    </div>
  );
}

function AllocationBar({ label, amount, percentage, color }) {
  const colorClasses = {
    green: 'bg-green-500 dark:bg-green-400',
    red: 'bg-red-500 dark:bg-red-400',
    blue: 'bg-blue-500 dark:bg-blue-400',
    purple: 'bg-purple-500 dark:bg-purple-400',
    cyan: 'bg-cyan-500 dark:bg-cyan-400',
    yellow: 'bg-yellow-500 dark:bg-yellow-400',
  };

  // Clamp the bar's visual width to 0–100% so over-allocated accounts
  // (e.g. 159.8%) don't bleed outside the card. We also clamp the displayed
  // percentage label to ">100%" or "<0%" to avoid visual shock, keeping the
  // exact value on hover for accuracy.
  const clampedWidth = Math.min(Math.max(percentage, 0), 100);
  const overAllocated = percentage > 100;
  const underwater = percentage < 0;
  const displayPercent = overAllocated
    ? '>100%'
    : underwater
    ? '<0%'
    : `${percentage.toFixed(1)}%`;
  const tooltipPercent = `${percentage.toFixed(1)}%`;

  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
        <span className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
          ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (
          <span title={`Exact: ${tooltipPercent}`} className="cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
            {displayPercent}
          </span>
          )
          {overAllocated && (
            <span
              className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40"
              data-testid="allocation-over-100"
              title={`This account currently exceeds its target allocation (exact: ${tooltipPercent}).`}
            >
              Above target
            </span>
          )}
          {underwater && (
            <span
              className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded bg-rose-500/20 text-rose-700 dark:text-rose-300 border border-rose-500/40"
              data-testid="allocation-underwater"
              title={`Exact: ${tooltipPercent}`}
            >
              Negative
            </span>
          )}
        </span>
      </div>
      <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
        <div
          className={`${colorClasses[color]} h-4 rounded-full transition-all`}
          style={{ width: `${clampedWidth}%` }}
        />
      </div>
    </div>
  );
}

export default AccountBalances;
